import { Url, IUrl, Singleton } from "app/utils";
import {
  Application,
  $Constants,
  $CurrentUser,
  $Router,
  $Analytics,
  stringify,
} from "app/classes";
import { $IdentityResource } from "app/resources";
import {
  IdentityService,
  $RavenService,
  intercomService,
  googleAnalyticsService,
  $LaunchDarklyService,
} from "app/services";
import { angular, app } from "app/ng/ng.module";
import { SETTINGS } from "./settings"
import { fromObject } from "url-env";
import auth0 from "auth0-js";

// We always try to load the previous token because otherwise
// the user would be logged out just because they refreshed the
// page
const previousAccessToken = localStorage.getItem('access_token') ?? undefined;

function getEnvironment () {
  try {
    return fromObject({
      'https://localhost:9300': {internal: 'local', external: "dev"},
      'https://dev.commerce.spscommerce.com': {internal: 'dev', external: "dev"},
      'https://test.commerce.spscommerce.com': {internal: 'test', external: "test"},
      'https://stage.commerce.spscommerce.com': {internal: 'stage', external: "stage"},
      'https://commerce.spscommerce.com': {internal: 'prod', external: "prod"}
    });
  } catch (e) {
    return {
      url: 'https://localhost:9300',
      env: {internal: 'local', external: "dev"}
    }
  }
}

// Turn urls into environments
const {url, env: auth0Env} = getEnvironment();

// Internally we know
const identityEnv = {
  internal: $Constants.env,
  external: $Constants.env === "local" ? "dev" : $Constants.env,
}

// This is our auth0 config. All of these are apps in the
// prod auth0 tennant
const apps = SETTINGS.AUTH0_CLIENTS;

export { auth0Env, apps, url };

// Create an instance of auth0-js using the correct environment
const auth = new auth0.WebAuth({ 
  ...apps[auth0Env.internal],
  domain: 'auth.spscommerce.com',
  audience: "api://api.spscommerce.com/",
  responseType: "token",
  scope: 'openid profile email', 
});

export class CommercePlatform {
  public ngLoaded = false;

  public init(): void {

    const startUp = ({tokenSource = 'identity', token, identityEnv, auth0Env, initialPath = `/launchpad/`}) => {

      // The token source determines which of the environments we are going to use
      const env = tokenSource === 'auth0' ? auth0Env : identityEnv;

      // We pass in the split environment so that we can use it to respond to 'local' in one
      // application event while replying as 'dev' to the other application event.
      // We set this before starting session because startSession because it is async and
      // and we want to prime the pump for the first render.
      $Router.setEnvironment(env);

      $Analytics.registerServices([
        intercomService,
        googleAnalyticsService
      ]);
      $Analytics.initServices();

      // This resource takes an actual resolved environment
      $IdentityResource.setEnvironment(env.external);

      this.registerPublicApps();
      // Passing in the hardcoded launchpad app
      // that we want to use at the top of the app
      // switcher.

      $CurrentUser
        .startSession({hardcodedLaunchpad: $Constants.hardcodedLaunchpad, env, tokenSource, token})
        .catch(() => {}) // noop for no session
        .finally($Router.gotoSavedRoute);

      $CurrentUser.onLogin(() => {
        this.loadAngularApp();
        $RavenService.setupUser($CurrentUser);
        $CurrentUser.apps.forEach($Router.registerApp);
        $Analytics.setUser($CurrentUser);
        $Router.broadcast("userAuthenticated", { token });
        $Router.goto(initialPath);
      });

      $CurrentUser.onLogout(() => {
        const wasTokenStored = Boolean(localStorage.getItem('access_token'));
        localStorage.removeItem('access_token');
        localStorage.removeItem('expires_at');
        $Router.broadcast("userUnauthenticated");
        $Router.unregisterApps();
        $LaunchDarklyService.logoutUser();
        // If the user does not have the correct org token, we want to retain
        // her savedRoute so that we can send her to it once she has the
        // correct org token.
        if ($CurrentUser.hasCorrectOrgToken()) {
          $Router.saveRoute();
        }
  
        // Determines if the url contains an org_id query param
        const currentUrl = Url.getCurrent();
        const currentUrlObj = Url.parse(currentUrl);
        const searchObj = Url.parseSearch(currentUrlObj.search);
        const orgId = searchObj["cp.orgid"];
        const orgId_queryParam = orgId ? `&cp.orgid=${orgId}` : "";
  
        if(wasTokenStored) {
          window.location.href = "/auth0/login/";
          return;
        }
        $Router.goto(`auth/login?${orgId_queryParam}`);
      });
    }

    // Redirect to auth0 login
    if(window.location.pathname === "/auth0/login/") {
      const initialFullUrl = new URLSearchParams('?'+$Router.savedRoute.fullpath.split('?')[1]).get('initialPath');
      
      let initialFullParsed;
      try {
        initialFullParsed = new URL(initialFullUrl);
      } catch (err) {
        // if it isn't a url we don't need to do anything because initialFullParsed
        // can just be undefined.
      }
      

      // use auth0 instead of cp login 
      auth.authorize({
          realm: apps[auth0Env.internal].realm,
          appState: stringify({
            initialPath: initialFullParsed?.pathname ?? '/launchpad/'
          }),
          redirectUri : url + '/auth0/callback'
      });
      return;
    }
    
    // Handle redirects back into the app from auth0
    // We can't have this branch with the other calls
    // to auth0 since it exists inside a callback
    if((window.location.pathname === "/auth0/callback" || window.location.pathname === "/auth0/callback/")) {
      auth.parseHash({ hash: window.location.hash }, (err, authResult) => {
          if (err) {
            throw new Error('Error parsing hash after callback: ' + err.error + ' Error decription: ' + err.description);
          }
          if (!err && authResult && authResult.accessToken) {
            const expiresAt = JSON.stringify(
              authResult.expiresIn * 1000 + new Date().getTime()
            );

            let appState;

            try {
              appState = JSON.parse(authResult.appState);
            } catch (err) {
              appState = {}
            }

            const initialPath = appState?.initialPath ?? '/launchpad/';
            // TODO: deal with refreshes
            //this.scheduleRenewal();

            localStorage.setItem('access_token', authResult.accessToken);
            localStorage.setItem('expires_at', expiresAt);
            startUp({tokenSource: "auth0", token: authResult.accessToken, auth0Env, identityEnv, initialPath});

          }
      });
      return;
    }

    const alwaysUseAuth0 = localStorage.getItem('alwaysUseAuth0') === "yes";
    const tokenSource = (previousAccessToken || alwaysUseAuth0) ? "auth0" : "identity";

    // Priority:
    //   - previously saved token
    //   - alwaysUseAuth0
    //   - identity
    const token = (previousAccessToken) ? previousAccessToken : (alwaysUseAuth0 ? undefined : $CurrentUser.token );
    startUp({tokenSource, token, auth0Env, identityEnv});
    return;
  }

  /**
   * Load the Angular portion of our application. This is deferred until the
   * user is logged in to save on load times.
   */
  public loadAngularApp(): void {
    if (!this.ngLoaded) {
      const bluebar = document.getElementById("bluebar");
      angular.bootstrap(bluebar, [app]);
      this.ngLoaded = true;
    }
  }

  private registerPublicApps() {
    const apps = $Constants.apps || [];
    apps.forEach((appDefinition) => {
      $Router.registerApp(new Application(appDefinition));
    });
  }
}

export let $CommercePlatform = Singleton("$CommercePlatform", CommercePlatform);
